import React from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  Divider,
} from '@mui/material';
import {
  AdminPermissionKey,
  getDefaultAdminPermissions,
  groupPermissionsForUI,
  ALL_ADMIN_PERMISSION_KEYS,
} from '../../constants/adminPermissions';

interface AdminPermissionsEditorProps {
  value: AdminPermissionKey[];
  onChange: (permissions: AdminPermissionKey[]) => void;
  onOpenClientAssignments?: () => void;
  disabled?: boolean;
}

const AdminPermissionsEditor: React.FC<AdminPermissionsEditorProps> = ({
  value,
  onChange,
  onOpenClientAssignments,
  disabled = false,
}) => {
  const groups = groupPermissionsForUI();
  const selected = new Set(value);

  const toggle = (key: AdminPermissionKey) => {
    if (selected.has(key)) {
      onChange(value.filter((k) => k !== key));
    } else {
      onChange([...value, key]);
    }
  };

  const selectAll = () => onChange([...ALL_ADMIN_PERMISSION_KEYS]);
  const resetDefault = () => onChange(getDefaultAdminPermissions());

  return (
    <Box sx={{ mt: 1 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Client data access is managed separately via Client Assignments (which clients this admin can see).
        {onOpenClientAssignments && (
          <Button size="small" onClick={onOpenClientAssignments} sx={{ ml: 1 }} disabled={disabled}>
            Manage Client Assignments
          </Button>
        )}
      </Alert>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button size="small" variant="outlined" onClick={resetDefault} disabled={disabled}>
          Reset to default
        </Button>
        <Button size="small" variant="outlined" onClick={selectAll} disabled={disabled}>
          Select all
        </Button>
      </Box>

      {groups.map((group, idx) => (
        <Box key={group.id} sx={{ mb: 2 }}>
          {idx > 0 && <Divider sx={{ mb: 2 }} />}
          <Typography variant="subtitle2" fontWeight={600}>
            {group.title}
          </Typography>
          {group.description && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {group.description}
            </Typography>
          )}
          <FormGroup>
            {group.permissions.map((perm) => (
              <FormControlLabel
                key={perm.key}
                control={
                  <Checkbox
                    checked={selected.has(perm.key)}
                    onChange={() => toggle(perm.key)}
                    disabled={disabled}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">{perm.label}</Typography>
                    {perm.description && (
                      <Typography variant="caption" color="text.secondary">
                        {perm.description}
                      </Typography>
                    )}
                  </Box>
                }
              />
            ))}
          </FormGroup>
        </Box>
      ))}
    </Box>
  );
};

export default AdminPermissionsEditor;
